import React, { useEffect, useState } from 'react';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { withInitiator } from '@src/api/util';
import { getLogger } from '@src/log-util';
import { isNullish } from '@src/util';
import { track } from '@src/utility/analytics';
import { InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { getCsrfToken, signIn, useSession } from 'next-auth/react';

import AuthLayout, {
  AUTH_DISPLAY_FONT,
  AUTH_LINK_COLOR,
  authFieldSx,
  authPrimaryButtonSx,
  authToggleButtonSx,
  Eyebrow,
  FieldLabel,
} from '../components/AuthLayout';
import SmsCode from '../components/SmsCode';

interface PageProps {
  csrfToken?: string;
}

export const getServerSideProps = withInitiator<PageProps>(async (context) => {
  const logger = getLogger('page.login.api');
  logger.info('Hello from the page');
  return {
    props: {
      csrfToken: await getCsrfToken(context),
    },
  };
});

export default function Login({ csrfToken = '' as string }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const {
    query: { error },
  } = router;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessageUsername] = useState('Please enter your email address');
  const [errorMessagePassword] = useState('Please enter your password');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [flow, setFlow] = useState('login');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'sms' | 'app'>('sms');
  const [errorOpen, setErrorOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const { status } = useSession();

  // Once the sign-in has established a session, send the user to the dashboard. Relying on this
  // (rather than the URL returned by signIn) avoids a race where router.push('/') can run before
  // the session cookie is committed, which bounced the user straight back to /login — making it
  // look like they had to sign in twice.
  useEffect(() => {
    if (status === 'authenticated') {
      router.push('/');
    }
  }, [status, router]);

  const handleErrorClose = (): void => {
    setErrorOpen(false);
  };

  async function handleLogin(): Promise<void> {
    if (username === '' || password === '') {
      setErrorOpen(true);
      return;
    }
    setErrorOpen(false);
    setSpinner(true);

    const response = await fetch('/api/sec/authenticate-user', {
      method: 'POST',
      body: JSON.stringify({
        username,
        password,
      }),
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await response.json();

    if (response.ok) {
      switch (body.type) {
        case 'error':
          setErrorMessage(body.message ?? 'Invalid email or password');
          break;
        case 'newPasswordRequired':
          setFlow('newPassword');
          break;
        case 'success':
          setTwoFactorMethod(body.twoFactorMethod === 'app' ? 'app' : 'sms');
          setFlow('code');
          break;
        default:
          setErrorMessage('Unknown error during the login');
          break;
      }
    } else {
      setErrorMessage(body.message ?? 'Unknown error during the login');
    }

    setSpinner(false);
  }

  return (
    <AuthLayout
      variant="card"
      badge="Secure sign-in"
      trust={['CDR-accredited', '110+ Australian banks', 'One API, sandbox included']}>
      {flow !== 'code' ? (
        <Box
          component="form"
          noValidate
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleLogin();
          }}>
          <Eyebrow>Developer portal</Eyebrow>
          <Typography
            component="h1"
            sx={{
              fontFamily: AUTH_DISPLAY_FONT,
              fontSize: { xs: '32px', sm: '36px' },
              lineHeight: 1.06,
              letterSpacing: '-0.028em',
              fontWeight: 700,
              color: '#091115',
              mb: 1.5,
            }}>
            Sign in.
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5 }}>
            Welcome back. Sign in to your Skript account.
          </Typography>

          <input name="csrfToken" type="hidden" defaultValue={csrfToken} />

          <Box sx={{ mb: 2.25 }}>
            <FieldLabel htmlFor="username">Email address</FieldLabel>
            <TextField
              id="username"
              name="username"
              type="email"
              placeholder="you@company.com.au"
              autoComplete="email"
              autoFocus
              fullWidth
              onChange={(e) => {
                setUsername(e.target.value);
              }}
              error={errorOpen && username === ''}
              helperText={errorOpen && username === '' ? errorMessageUsername : undefined}
              sx={authFieldSx}
            />
          </Box>

          <Box>
            <FieldLabel
              htmlFor="password"
              action={
                <Link
                  href="/forgot-password"
                  sx={{ fontSize: '13px', fontWeight: 500, color: AUTH_LINK_COLOR, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              }>
              Password
            </FieldLabel>
            <TextField
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="current-password"
              fullWidth
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              error={errorOpen && password === ''}
              helperText={errorOpen && password === '' ? errorMessagePassword : undefined}
              sx={authFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      edge="end"
                      onClick={() => {
                        setShowPassword((current) => !current);
                      }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      sx={authToggleButtonSx}>
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mt: 3.5, mb: 2.25 }}>
            <Button type="submit" variant="contained" fullWidth disabled={spinner} sx={authPrimaryButtonSx}>
              {spinner ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Don&apos;t have an account yet?{' '}
            <Link href="/signup/details" sx={{ textDecoration: 'none', fontWeight: 600, color: AUTH_LINK_COLOR }}>
              Sign up
            </Link>
          </Typography>

          <Snackbar
            open={errorOpen}
            autoHideDuration={6000}
            onClose={() => {
              handleErrorClose();
            }}>
            <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
              Please complete all required fields{error != null ? ` - ${error as string}` : ''}
            </Alert>
          </Snackbar>
        </Box>
      ) : (
        <SmsCode
          title="Confirm it's you."
          subtitle={
            twoFactorMethod === 'app'
              ? 'Please enter the 6 digit code from your authenticator app.'
              : 'Please enter the 6 digit authentication code that was sent to your phone number.'
          }
          loading={spinner}
          showResend={twoFactorMethod === 'sms'}
          actions={{
            signIn: async (code: string) => {
              setSpinner(true);
              const signInResponse = await signIn('code-login', {
                callbackUrl: '/',
                redirect: false,
                username,
                password,
                code,
              });

              if (!isNullish(signInResponse?.error)) {
                setSpinner(false);
                try {
                  const errorBlock = JSON.parse(signInResponse?.error ?? '');
                  setErrorMessage(errorBlock.errorMessage);
                } catch (e) {
                  setErrorMessage('Error during code verification');
                }
              } else if (signInResponse?.ok === true) {
                track('login_completed', { method: twoFactorMethod });
                // Go straight to the dashboard on success. We deliberately ignore
                // signInResponse.url: the NextAuth `redirect` callback returns the bare
                // origin (baseUrl), which router.push mishandles and bounces the
                // just-authenticated user back to /login — making it look like they have to
                // sign in a second time.
                router.push('/');
              }
            },
            resend:
              twoFactorMethod === 'sms'
                ? async () => {
                    setSpinner(true);

                    const response = await fetch('/api/sec/resend-code', {
                      method: 'POST',
                      body: JSON.stringify({
                        username,
                        password,
                      }),
                      headers: { 'Content-Type': 'application/json' },
                    });
                    const resendBody = await response.json();

                    setSpinner(false);

                    if (!response.ok) {
                      setErrorMessage(resendBody.message);
                    }
                  }
                : undefined,
          }}
        />
      )}
      <Snackbar
        open={!(errorMessage == null)}
        autoHideDuration={6000}
        onClose={() => {
          setErrorMessage(null);
        }}>
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          Login error - {errorMessage}
        </Alert>
      </Snackbar>
    </AuthLayout>
  );
}

Login.auth = false;
